import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Save, X, DollarSign, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axiosInstance from '@/utils/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { formatPrice } from '@/utils/formatters';

const AdminPaymentPlansPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    price: '',
    duration_days: '',
    description: '',
    enabled: true,
    display_order: 0
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      toast.error('Acceso denegado');
      return;
    }
    fetchPlans();
  }, [isAdmin]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/admin/payment-plans');
      setPlans(data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Error al cargar los planes');
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    try {
      await axiosInstance.post('/admin/payment-plans/initialize');
      toast.success('Planes inicializados correctamente');
      fetchPlans();
    } catch (error) {
      console.error('Error initializing plans:', error);
      toast.error(error.response?.data?.detail || 'Error al inicializar planes');
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan.type);
    setFormData({
      type: plan.type,
      name: plan.name,
      price: plan.price,
      duration_days: plan.duration_days,
      description: plan.description,
      enabled: plan.enabled,
      display_order: plan.display_order || 0
    });
  };

  const handleCancelEdit = () => {
    setEditingPlan(null);
    setFormData({
      type: '',
      name: '',
      price: '',
      duration_days: '',
      description: '',
      enabled: true,
      display_order: 0
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.price || !formData.duration_days) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    try {
      await axiosInstance.put(`/admin/payment-plans/${editingPlan}`, {
        name: formData.name,
        price: parseFloat(formData.price),
        duration_days: parseInt(formData.duration_days),
        description: formData.description,
        enabled: formData.enabled,
        display_order: parseInt(formData.display_order)
      });
      
      toast.success('Plan actualizado correctamente');
      setEditingPlan(null);
      fetchPlans();
    } catch (error) {
      console.error('Error updating plan:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar el plan');
    }
  };

  const handleToggleEnabled = async (planType, currentEnabled) => {
    try {
      await axiosInstance.put(`/admin/payment-plans/${planType}`, {
        enabled: !currentEnabled
      });
      
      toast.success(`Plan ${!currentEnabled ? 'habilitado' : 'deshabilitado'}`);
      fetchPlans();
    } catch (error) {
      console.error('Error toggling plan:', error);
      toast.error('Error al cambiar el estado del plan');
    }
  };

  if (!isAdmin) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-2">
                Configuración de Planes de Pago
              </h1>
              <p className="text-slate-600">Administra los precios y planes de tu plataforma</p>
            </div>
            {plans.length === 0 && (
              <Button onClick={handleInitialize} className="bg-primary hover:bg-primary-hover">
                <Plus className="w-4 h-4 mr-2" />
                Inicializar Planes por Defecto
              </Button>
            )}
          </div>

          {plans.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="payment-plans-grid">
              {plans.map((plan) => (
                <motion.div
                  key={plan.type}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className={`relative ${!plan.enabled ? 'opacity-60' : ''}`}>
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant={plan.enabled ? 'default' : 'secondary'}>
                          {plan.enabled ? 'Activo' : 'Inactivo'}
                        </Badge>
                        <div className="flex space-x-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleToggleEnabled(plan.type, plan.enabled)}
                            data-testid={`toggle-${plan.type}`}
                          >
                            {plan.enabled ? (
                              <XCircle className="w-4 h-4 text-red-500" />
                            ) : (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(plan)}
                            data-testid={`edit-${plan.type}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <CardTitle className="text-xl font-heading">{plan.name}</CardTitle>
                      <CardDescription className="text-xs font-mono text-slate-500">
                        Tipo: {plan.type}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {editingPlan === plan.type ? (
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor={`name-${plan.type}`}>Nombre del Plan</Label>
                            <Input
                              id={`name-${plan.type}`}
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`price-${plan.type}`}>Precio (COP)</Label>
                            <Input
                              id={`price-${plan.type}`}
                              type="number"
                              value={formData.price}
                              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`duration-${plan.type}`}>Duración (días)</Label>
                            <Input
                              id={`duration-${plan.type}`}
                              type="number"
                              value={formData.duration_days}
                              onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`description-${plan.type}`}>Descripción</Label>
                            <Textarea
                              id={`description-${plan.type}`}
                              value={formData.description}
                              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                              className="mt-1"
                              rows={2}
                            />
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              onClick={handleSave}
                              className="flex-1 bg-primary hover:bg-primary-hover"
                            >
                              <Save className="w-4 h-4 mr-2" />
                              Guardar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEdit}
                              className="flex-1"
                            >
                              <X className="w-4 h-4 mr-2" />
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="mb-4">
                            <div className="flex items-baseline mb-2">
                              <DollarSign className="w-5 h-5 text-primary mr-1" />
                              <span className="text-3xl font-heading font-bold text-primary">
                                {formatPrice(plan.price)}
                              </span>
                            </div>
                            <div className="flex items-center text-slate-600 text-sm">
                              <Clock className="w-4 h-4 mr-1" />
                              <span>{plan.duration_days} días</span>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600">{plan.description}</p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
              <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-xl text-slate-600 mb-4">No hay planes configurados</p>
              <p className="text-slate-500 mb-6">Inicializa los planes por defecto para comenzar</p>
              <Button onClick={handleInitialize} className="bg-primary hover:bg-primary-hover">
                <Plus className="w-4 h-4 mr-2" />
                Inicializar Planes
              </Button>
            </div>
          )}

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="font-heading font-bold text-blue-900 mb-2">ℹ️ Información</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Los cambios en los precios se aplican inmediatamente a nuevos pagos</li>
              <li>• Los pagos existentes mantendrán el precio con el que fueron creados</li>
              <li>• Deshabilitar un plan lo oculta de los usuarios pero no afecta pagos activos</li>
              <li>• El tipo de plan no puede ser modificado una vez creado</li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPaymentPlansPage;
